import dayjs from 'dayjs';
import { Input, Textarea } from '@vechaiui/react';
import { Localized, useLocalization } from '@fluent/react';
import { forwardRef, useEffect } from 'react';
import { useField } from 'formik';

import CargoDetailsForm from './CargoDetailsForm';
import { ContainerPacking, Vehicle } from '#/__generated__/graphql/graphql';
import { DatePickerFormik } from '#/components/controls/DatePicker';
import { DropzoneFormik } from '../controls/Dropzone';
import { InputFormik } from '#/components/controls/Input';
import { RouteLegSingleSideForm } from './RouteLegForm';
import { SwitchFormik } from '#/components/controls/Switch';
import { VehicleListboxFormik } from '#/components/controls/VehicleListbox';

/* TODO
 * - [ ] Add checking to check if the part of the form filled up, update the stepper on top and the one on the left. The number needs to change to CHECK icon when done.
 * - [ ] Add timeline to the side number.
 * - [] Get the timelist from API
 * - [X] Make the Delivery Time cannot be before Pick Up Time
 * - [X] Remove AGENT ID and CustomerListBox
 * - [X] Cargo Attachment
 * - [ ] Limit the cargo attachment
 * - [X] Display the file preview
 * - [X] Replace the file
 */

const RequestForQuotationForm: React.FC<RequestForQuotationFormProps> = ({ name }) => {
  const { l10n } = useLocalization();
  const namePrefix = name ? `${name}.` : '';
  const [preferredContainerPacking] = useField(`${namePrefix}preferredContainerPacking`);
  const isLcl = preferredContainerPacking.value === ContainerPacking.LessThanContainerLoad;

  const [contentFile] = useField(`${namePrefix}attachments`);
  const [, , deliverTimeHelper] = useField(`${namePrefix}deliveryTime`);
  const [pickupTime] = useField(`${namePrefix}pickupTime`);

  useEffect(() => {
    deliverTimeHelper.setValue(undefined);
  }, [pickupTime.value]);

  return (
    <div className="space-y-12">
      <fieldset>
        <header className="flex items-center gap-4 mb-6">
          <div>
            <span className="inline-flex items-center justify-center w-5 h-5 border border-black rounded-full">
              1
            </span>
          </div>

          <div className="flex-1">
            <h2 className="text-2xl font-bold">{l10n.getString('pick-up')}</h2>
          </div>
        </header>

        <RouteLegSingleSideForm name="routeLegs.0" side="start" />
      </fieldset>

      <fieldset>
        <header className="flex items-center gap-4 mb-6">
          <div>
            <span className="inline-flex items-center justify-center w-5 h-5 border border-black rounded-full">
              2
            </span>
          </div>

          <div className="flex-1">
            <h2 className="text-2xl font-bold">{l10n.getString('destination')}</h2>
          </div>
        </header>

        <RouteLegSingleSideForm name="routeLegs.0" side="end" />
      </fieldset>

      <fieldset>
        <header className="flex items-center gap-4 mb-6">
          <div>
            <span className="inline-flex items-center justify-center w-5 h-5 border border-black rounded-full">
              3
            </span>
          </div>

          <div className="flex-1">
            <h2 className="text-2xl font-bold">{l10n.getString('cargo-details')}</h2>
          </div>
        </header>

        <div className="space-y-4">
          {!isLcl && (
            <div>
              <Localized id="truck-type" attrs={{ placeholder: true }}>
                <VehicleListboxFormik
                  getKey={(item: Omit<Vehicle, 'id'>) => item.name}
                  // TODO: simplify ideally -> item => item, use sanitizer?
                  getValue={({ id, ...item }) => ({
                    ...item,
                    cargoVolume: {
                      unitCode: item.cargoVolume?.unitCode,
                      value: item.cargoVolume?.value,
                    },
                  })}
                  label={l10n.getString('truck-type')}
                  name={`${namePrefix}preferredVehicle`}
                />
              </Localized>
            </div>
          )}

          {/* TODO: Temporary comment off - Plank-web issue 217 */}
          {/*
          <div>
            <ContainerPackingSwitchFormik
              name={`${namePrefix}preferredContainerPacking`}
              rightText={l10n.getString(ContainerPacking.LessThanContainerLoad)}
            />
          </div>
          */}

          <CargoDetailsForm name="cargoDetails" />

          <div>
            <p className="mb-2">UPLOAD ATTACHMENTS</p>
            <div className="space-y-2">
              {/* <InputFormik name={`${namePrefix}attachments`} type="file" /> */}
              <DropzoneFormik
                accept={{ 'image/*': [], 'application/pdf': [] }}
                content={"Drag 'n' drop some files here, or click to select files"}
                name={`${namePrefix}attachments`}
                // subtext={'(Only images file will be accepted)'}
              />
              {!!contentFile && !!contentFile.value && (
                <div className="text-sm italic text-neutral-500">
                  Total File(s) Selected:{' '}
                  {!!contentFile ? (!!contentFile.value.length ? contentFile.value.length : 1) : 0}
                </div>
              )}
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <header className="flex items-center gap-4 mb-6">
          <div>
            <span className="inline-flex items-center justify-center w-5 h-5 border border-black rounded-full">
              4
            </span>
          </div>

          <div className="flex-1">
            <h2 className="text-2xl font-bold">{l10n.getString('date')}</h2>
          </div>
        </header>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Localized id="pickup-date" attrs={{ placeholder: true }}>
                <DatePickerFormik
                  customInput={<CustomDateInput />}
                  dateFormat="MMM dd, yyyy h:mm aa"
                  filterTime={filterTime}
                  label={l10n.getString('pickup-date')}
                  maxTime={dayjs().hour(22).minute(0).toDate()}
                  minDate={new Date()}
                  minTime={dayjs().hour(8).minute(0).toDate()}
                  name={`${namePrefix}pickupTime`}
                  showTimeSelect
                />
              </Localized>
            </div>

            <div>
              <Localized id="delivery-date" attrs={{ placeholder: true }}>
                <DatePickerFormik
                  customInput={<CustomDateInput />}
                  dateFormat="MMM dd, yyyy h:mm aa"
                  disabled={!pickupTime.value}
                  filterTime={(time) => filterDeliveryTime(time, pickupTime.value)}
                  label={l10n.getString('delivery-date')}
                  maxTime={dayjs().hour(22).minute(0).toDate()}
                  minDate={new Date()}
                  minTime={dayjs().hour(8).minute(0).toDate()}
                  name={`${namePrefix}deliveryTime`}
                  showTimeSelect
                />
              </Localized>
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <header className="flex items-center gap-4 mb-6">
          <div>
            <span className="inline-flex items-center justify-center w-5 h-5 border border-black rounded-full">
              5
            </span>
          </div>

          <div className="flex-1">
            <h2 className="text-2xl font-bold">{l10n.getString('additional-note')}</h2>
          </div>
        </header>

        <div className="space-y-4">
          <div>
            <Localized id="additional-note" attrs={{ placeholder: true }}>
              <InputFormik as={Textarea} name={`${namePrefix}note`} />
            </Localized>
          </div>

          <div>
            <SwitchFormik
              name="insuranceIncluded"
              rightText={l10n.getString('insurance-coverage')}
            />
          </div>
        </div>
      </fieldset>
    </div>
  );
};
export type RequestForQuotationFormProps = {
  name?: string;
};

const CustomDateInput = forwardRef<
  HTMLElement,
  { value?: Date | null; onClick?: React.MouseEventHandler<HTMLInputElement> }
>(({ value, ...props }, ref) => {
  const extendedValue = value ? dayjs(value).add(2, 'hours').format('h:mm A') : '';
  return (
    <Input size="lg" {...props} ref={ref} value={value ? `${value} - ${extendedValue}` : ''} />
  );
});

const filterTime = (time: Date) => {
  return dayjs(time).isAfter(dayjs().add(6, 'hour')); // Client requested to add 6 hours for the available booking time.
};

const filterDeliveryTime = (time: Date, pickupTime: Date) => {
  return dayjs(time).isAfter(dayjs(pickupTime).add(2, 'h'));
};

export default RequestForQuotationForm;
